export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_BATCH_BYTES = 100 * 1024 * 1024;
export const MAX_FILES = 12;
export const MAX_PIXELS = 50_000_000;

const HEIC_BRANDS = new Set([
  'heic',
  'heix',
  'hevc',
  'hevx',
  'heim',
  'heis',
  'hevm',
  'hevs',
  'heif',
]);
const GENERIC_HEIF_BRANDS = new Set(['mif1', 'msf1']);
const AVIF_BRANDS = new Set(['avif', 'avis']);

export interface HeifSniffResult {
  valid: boolean;
  brands: string[];
}

export interface ImageDimensions {
  width: number;
  height: number;
  pixels: number;
}

export interface FileValidation {
  ok: boolean;
  error?: string;
  dimensions?: ImageDimensions;
}

function readUint32(bytes: Uint8Array, offset: number): number {
  return (
    bytes[offset] * 0x1000000 +
    (bytes[offset + 1] << 16) +
    (bytes[offset + 2] << 8) +
    bytes[offset + 3]
  );
}

function ascii(bytes: Uint8Array, start: number, length: number): string {
  return String.fromCharCode(...bytes.subarray(start, start + length));
}

export function sniffHeic(bytes: Uint8Array): HeifSniffResult {
  let offset = 0;

  while (offset + 12 <= bytes.length && offset < 4096) {
    const size = readUint32(bytes, offset);
    const type = ascii(bytes, offset + 4, 4);

    if (size < 8 || offset + size > bytes.length) {
      break;
    }

    if (type === 'ftyp' && size >= 16) {
      const brands = [ascii(bytes, offset + 8, 4)];
      for (let cursor = offset + 16; cursor + 4 <= offset + size; cursor += 4) {
        brands.push(ascii(bytes, cursor, 4));
      }
      const isAvif = brands.some((brand) => AVIF_BRANDS.has(brand));
      const isHeic = brands.some((brand) => HEIC_BRANDS.has(brand));
      const isGenericHeif = brands.some((brand) =>
        GENERIC_HEIF_BRANDS.has(brand),
      );
      return { valid: !isAvif && (isHeic || isGenericHeif), brands };
    }

    offset += size;
  }

  return { valid: false, brands: [] };
}

export function findHeifDimensions(
  bytes: Uint8Array,
): ImageDimensions | undefined {
  for (let marker = 4; marker + 16 <= bytes.length; marker += 1) {
    if (ascii(bytes, marker, 4) !== 'ispe') continue;

    const boxStart = marker - 4;
    const size = readUint32(bytes, boxStart);
    if (size < 20 || boxStart + size > bytes.length) continue;

    const width = readUint32(bytes, marker + 8);
    const height = readUint32(bytes, marker + 12);
    if (width === 0 || height === 0) continue;

    return { width, height, pixels: width * height };
  }

  return undefined;
}

export async function validateHeicFile(file: File): Promise<FileValidation> {
  if (file.size === 0) {
    return { ok: false, error: 'This file is empty.' };
  }

  if (file.size > MAX_FILE_BYTES) {
    return { ok: false, error: 'This file is over the 25 MB limit.' };
  }

  const header = new Uint8Array(
    await file.slice(0, Math.min(file.size, 2 * 1024 * 1024)).arrayBuffer(),
  );
  if (!sniffHeic(header).valid) {
    return {
      ok: false,
      error: 'This is not a supported HEIC or HEIF file.',
    };
  }

  const dimensions = findHeifDimensions(header);
  if (dimensions && dimensions.pixels > MAX_PIXELS) {
    return {
      ok: false,
      error: 'This photo is over the 50-megapixel limit.',
      dimensions,
    };
  }

  return { ok: true, dimensions };
}
